// Per-style schema for the Touchpoint blocks editor.
//
// Each style has its own set of named blocks (kicker, tagline, bubble, etc.).
// The Editor renders one input per block in order. The Preview reads the
// resolved value via getBlock() which falls back to DEFAULT_BLOCKS if the
// touchpoint has no override stored in body_template.blocks[key].

export type TpStyle =
  | "plain"
  | "wanted"
  | "drama"
  | "sticker"
  | "info"
  | "pop"
  | "neon"
  | "tabloid"
  | "burst"
  | "mono";

export type BlockDef = {
  key: string;
  label: string;
  help?: string;
  type: "text" | "textarea";
  /** Show a "Insert {prénom}" helper button below the input */
  vars?: boolean;
  /** Default rows for textarea */
  rows?: number;
};

export const BLOCKS_SCHEMA: Record<TpStyle, BlockDef[]> = {
  plain: [
    { key: "title", label: "Sujet du mail", type: "text", vars: true },
    {
      key: "body",
      label: "Corps du mail (paragraphes séparés par 2 sauts de ligne)",
      type: "textarea",
      vars: true,
      rows: 10,
    },
  ],
  wanted: [
    { key: "title", label: "Sujet du mail", type: "text", vars: true },
    {
      key: "kicker",
      label: "Bandeau haut",
      help: "Petit bandeau noir tout en haut de l'image",
      type: "text",
    },
    {
      key: "tagline_main",
      label: "Tagline · ligne 1 (en gras)",
      type: "text",
    },
    {
      key: "tagline_sub",
      label: "Tagline · ligne 2",
      help: "Le mot ou groupe mis en jaune va dans le champ suivant",
      type: "text",
    },
    {
      key: "tagline_em",
      label: "Mot ou groupe mis en jaune (em)",
      type: "text",
    },
    {
      key: "bubble",
      label: "Bulle de dialogue (le vrai corps du message)",
      type: "textarea",
      vars: true,
      rows: 4,
    },
    { key: "video_meta_top", label: "Titre de la vidéo", type: "text" },
    { key: "video_meta_sub", label: "Sous-titre vidéo (ex: par Jérémy · 1 min)", type: "text" },
    { key: "signoff", label: "Signature de fin", type: "text" },
  ],
  drama: [
    { key: "title", label: "Sujet du mail", type: "text", vars: true },
    { key: "kicker_left", label: "Bandeau noir · côté gauche", type: "text" },
    { key: "kicker_right", label: "Bandeau noir · côté droit (vol/no)", type: "text" },
    {
      key: "tagline_main",
      label: "Tagline · avant le span jaune",
      type: "text",
    },
    { key: "tagline_em", label: "Span jaune (mot mis en avant)", type: "text" },
    { key: "tagline_sub", label: "Sous-tagline", type: "text" },
    {
      key: "bubble",
      label: "Bulle de dialogue (le corps)",
      type: "textarea",
      vars: true,
      rows: 4,
    },
    { key: "video_badge", label: "Badge sur la vidéo (ex: ▸ LOOM · 1:00)", type: "text" },
    { key: "video_meta_top", label: "Titre vidéo", type: "text" },
    { key: "video_meta_sub", label: "Sous-titre vidéo", type: "text" },
    { key: "signoff", label: "Signature de fin", type: "text" },
  ],
  sticker: [
    { key: "title", label: "Sujet du mail", type: "text", vars: true },
    { key: "kicker", label: "Bandeau (ex: ★ Newsletter LLYLL · ed.04)", type: "text" },
    { key: "tagline_main", label: "Tagline · ligne 1 (en gras)", type: "text" },
    { key: "tagline_em", label: "Mot/groupe mis en avant (em)", type: "text" },
    { key: "tagline_sub", label: "Suite de la tagline", type: "text" },
    { key: "tagline_attribution", label: "Attribution (ex: — le Chief)", type: "text" },
    {
      key: "bubble",
      label: "Bulle jaune (le corps)",
      type: "textarea",
      vars: true,
      rows: 4,
    },
    { key: "video_badge", label: "Badge sur la vidéo", type: "text" },
    { key: "video_meta_top", label: "Titre vidéo", type: "text" },
    { key: "video_meta_sub", label: "Sous-titre vidéo", type: "text" },
    { key: "signoff", label: "Signature de fin", type: "text" },
  ],
  info: [
    { key: "title", label: "Sujet du mail", type: "text", vars: true },
    { key: "kicker", label: "Bandeau (ex: ⚡ Info · partagée par Jérémy)", type: "text" },
    { key: "lead_in", label: "Phrase d'intro", type: "text", vars: true },
    { key: "topics_intro", label: "Intro des thèmes", type: "text" },
    { key: "sujet1", label: "Sujet 1 (highlight)", type: "text" },
    { key: "sujet2", label: "Sujet 2 (highlight)", type: "text" },
    { key: "sujet3", label: "Sujet 3 (highlight)", type: "text" },
    { key: "video_meta_top", label: "Titre vidéo", type: "text" },
    { key: "video_meta_sub", label: "Sous-titre vidéo", type: "text" },
    {
      key: "question",
      label: "Question finale (CTA)",
      type: "textarea",
      rows: 3,
    },
    { key: "signoff", label: "Signature de fin", type: "text" },
  ],
  pop: [
    { key: "title", label: "Sujet du mail", type: "text", vars: true },
    { key: "kicker", label: "Bandeau néon (en haut)", type: "text" },
    { key: "headline", label: "Headline énorme (en majuscules)", type: "text", vars: true },
    { key: "tagline_sub", label: "Sous-headline (couleur cyan)", type: "text" },
    { key: "bubble", label: "Message principal (cartouche jaune)", type: "textarea", vars: true, rows: 4 },
    { key: "cta_text", label: "Texte du gros bouton vidéo", type: "text" },
    { key: "video_meta_top", label: "Titre vidéo (en dessous)", type: "text" },
    { key: "video_meta_sub", label: "Sous-titre vidéo", type: "text" },
    { key: "signoff", label: "Signature de fin", type: "text" },
  ],
  neon: [
    { key: "title", label: "Sujet du mail", type: "text", vars: true },
    { key: "kicker", label: "Bandeau néon (en haut, monospace)", type: "text" },
    { key: "headline", label: "Headline (effet glow cyan/magenta)", type: "text", vars: true },
    { key: "tagline_sub", label: "Sous-tagline (vert électrique)", type: "text" },
    { key: "bubble", label: "Message (carte sombre)", type: "textarea", vars: true, rows: 4 },
    { key: "cta_text", label: "Texte du CTA", type: "text" },
    { key: "video_meta_top", label: "Titre vidéo", type: "text" },
    { key: "video_meta_sub", label: "Sous-titre vidéo", type: "text" },
    { key: "signoff", label: "Signature de fin", type: "text" },
  ],
  tabloid: [
    { key: "title", label: "Sujet du mail", type: "text", vars: true },
    { key: "kicker", label: "Petit bandeau rouge", type: "text" },
    { key: "headline", label: "GROS TITRE SCREAMER", type: "text", vars: true },
    { key: "subhead", label: "Sous-titre (petit, en italique)", type: "text" },
    { key: "lead", label: "Lead paragraphe (le hook)", type: "textarea", vars: true, rows: 3 },
    { key: "bubble", label: "Boîte de citation", type: "textarea", vars: true, rows: 3 },
    { key: "video_meta_top", label: "Titre vidéo", type: "text" },
    { key: "video_meta_sub", label: "Sous-titre vidéo", type: "text" },
    { key: "signoff", label: "Signature de fin", type: "text" },
  ],
  burst: [
    { key: "title", label: "Sujet du mail", type: "text", vars: true },
    { key: "kicker", label: "Bandeau pop art", type: "text" },
    { key: "burst_word", label: "Mot dans l'étoile éclatée (court !)", type: "text" },
    { key: "headline", label: "Headline (gros, dramatique)", type: "text", vars: true },
    { key: "bubble", label: "Bulle de dialogue", type: "textarea", vars: true, rows: 4 },
    { key: "cta_text", label: "Texte CTA", type: "text" },
    { key: "video_meta_top", label: "Titre vidéo", type: "text" },
    { key: "video_meta_sub", label: "Sous-titre vidéo", type: "text" },
    { key: "signoff", label: "Signature de fin", type: "text" },
  ],
  mono: [
    { key: "title", label: "Sujet du mail", type: "text", vars: true },
    { key: "kicker", label: "Numéro édition / kicker", type: "text" },
    { key: "headline", label: "Headline (typo massive)", type: "text", vars: true },
    { key: "subhead", label: "Sous-titre (mince)", type: "text" },
    { key: "body", label: "Corps (paragraphes séparés par \\n\\n)", type: "textarea", vars: true, rows: 6 },
    { key: "highlight", label: "Citation mise en avant (jaune)", type: "text", vars: true },
    { key: "video_meta_top", label: "Titre vidéo", type: "text" },
    { key: "video_meta_sub", label: "Sous-titre vidéo", type: "text" },
    { key: "signoff", label: "Signature de fin", type: "text" },
  ],
};

export const DEFAULT_BLOCKS: Record<TpStyle, Record<string, string>> = {
  plain: {
    body:
      "Hello {prénom},\n\nJ'espère que tu vas bien !\n\nJe voulais te partager cette info.\n\nJ'espère qu'on se parle prochainement !\n\nÀ très vite,\nJérémy",
  },
  wanted: {
    kicker: "// Newsletter · LLYLL",
    tagline_main: "Jérémy Harroch, un message pour toi.",
    tagline_sub: "T'as intérêt de le lire, sinon t'auras affaire à",
    tagline_em: "moi.",
    bubble: "Hello {prénom}, j'espère que tu vas bien. Je voulais te partager cette information : …",
    video_meta_top: "Lecture du message",
    video_meta_sub: "par Jérémy · 1 min",
    signoff: "— LLYLL · Uncorporate by design",
  },
  drama: {
    kicker_left: "· LLYLL · The Chief speaks",
    kicker_right: "VOL. 01 / NO. 02",
    tagline_main: "Jérémy Harroch,",
    tagline_em: "un message pour toi.",
    tagline_sub: "T'as intérêt de le lire, sinon t'auras affaire à moi.",
    bubble:
      "Hello {prénom}, j'espère que tu vas bien. Je voulais te partager cette information : …",
    video_badge: "▸ LOOM · 1:00",
    video_meta_top: "Le message en vidéo",
    video_meta_sub: "Jérémy · ~1 min",
    signoff: "— LLYLL · Uncorporate by design",
  },
  sticker: {
    kicker: "★ Newsletter LLYLL · ed.04",
    tagline_main: "Jérémy Harroch, un message pour toi.",
    tagline_em: "T'as intérêt de le lire,",
    tagline_sub: "sinon t'auras affaire à moi.",
    tagline_attribution: "— le Chief, qui te regarde.",
    bubble: "Hello {prénom}, j'espère que tu vas bien. Je voulais te partager cette information : …",
    video_badge: "★ 1 MIN · LIRE",
    video_meta_top: "Le message complet",
    video_meta_sub: "1 min · Jérémy",
    signoff: "— LLYLL · Uncorporate by design",
  },
  info: {
    kicker: "⚡ Info · partagée par Jérémy",
    lead_in: "Hé {prénom}, j'espère que tu vas bien. Juste pour te partager cette info :",
    topics_intro: "Voici une vidéo qui parle de",
    sujet1: "Vivatech",
    sujet2: "startups françaises",
    sujet3: "AI européen",
    video_meta_top: "Voir la vidéo",
    video_meta_sub: "~45 sec",
    question:
      "Dis-moi si ça t'intéresse d'en parler plus. Ou penses-tu qu'il y a certaines personnes que ça pourrait intéresser pour approfondir ?",
    signoff: "— Jérémy · LLYLL",
  },
  pop: {
    kicker: "✦✦ POP NEWSLETTER ✦✦ ED. 01",
    headline: "HEY {prénom},\nT'AS UNE SECONDE ?",
    tagline_sub: "↓ Cliquez. Ne réfléchissez pas trop. ↓",
    bubble:
      "Vous l'avez senti ? Ce truc dans l'air ? C'est l'odeur d'une bonne idée qui passe. Profitez-en avant qu'elle parte.",
    cta_text: "▶ JOUER LA VIDÉO",
    video_meta_top: "Le truc que vous devez voir",
    video_meta_sub: "1 min · Jérémy",
    signoff: "— Jérémy · LLYLL · POP edition",
  },
  neon: {
    kicker: "// LLYLL.MAINFRAME · CES_2026.LOG",
    headline: "{prénom},\n>>> SIGNAL DÉTECTÉ",
    tagline_sub: "[BANDWIDTH: 100%] · streaming Las Vegas to you",
    bubble:
      "On a vu un truc à Vegas qui change la donne. 3 startups, 1 angle, beaucoup de signaux pour la suite. Te transmets le flux ↓",
    cta_text: "▶ DECRYPT TRANSMISSION",
    video_meta_top: "CES 2026 · Field report",
    video_meta_sub: "1 min · canal Jérémy",
    signoff: "// END_OF_FILE — LLYLL · v.1",
  },
  tabloid: {
    kicker: "★ ÉDITION SPÉCIALE ★",
    headline: "PRETTO + LLYLL :\n600 K€ EN UN COUP ?!",
    subhead: "Ce qu'on a découvert va changer la com de toutes les fintechs",
    lead:
      "{prénom}, la rumeur est confirmée. Voici les chiffres exacts, le pitch retravaillé et la vidéo qui résume tout. Lecture en 1 min.",
    bubble:
      "« Si je l'avais su plus tôt, on aurait pu doubler la mise. » — un dirigeant Pretto, hors-micro.",
    video_meta_top: "L'enquête vidéo · 1 min",
    video_meta_sub: "présentée par Jérémy",
    signoff: "LLYLL · le tabloid sérieux",
  },
  burst: {
    kicker: "⚡ INCYBER REPLAY · 2026 ⚡",
    burst_word: "BANG !",
    headline: "{prénom},\nÇA A CARTONNÉ.",
    bubble:
      "L'event est passé, le best-of est là. 90 secondes, les 3 idées qui ressortent, et un angle pour ta com B2B cyber. Dis-moi si ça résonne.",
    cta_text: "VOIR LE REPLAY ▸",
    video_meta_top: "INCYBER best-of · 90s",
    video_meta_sub: "monté par Jérémy",
    signoff: "— LLYLL · l'autre regard",
  },
  mono: {
    kicker: "Ed. 05 · X-Forum 2026",
    headline: "{prénom},\non a passé une journée\nentre tes futur·e·s collègues.",
    subhead:
      "Voici ce qu'il faut savoir sur l'édition 2026, et 3 idées pour ton équipe recrutement.",
    body:
      "On a parlé à des centaines d'étudiant·e·s ingénieur. Trois angles ressortent.\n\nUn : iels veulent du concret, pas du gloss. Deux : iels regardent la marque employeur avant le poste. Trois : iels aiment quand on les filme dignement (et c'est rare).\n\nDans la vidéo, le résumé visuel en 60 sec.",
    highlight: "« Si une boîte fait l'effort de me filmer, je regarde sa marque. »",
    video_meta_top: "X-Forum 2026 — résumé · 1 min",
    video_meta_sub: "par Jérémy",
    signoff: "— Jérémy · LLYLL",
  },
};

/** Read a block from a touchpoint's body_template.blocks, with fallback to default. */
export function getBlock(
  style: TpStyle,
  blocks: Record<string, string> | undefined,
  key: string,
): string {
  const override = blocks?.[key];
  if (override !== undefined && override !== null) return override;
  return DEFAULT_BLOCKS[style]?.[key] ?? "";
}
